import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as CheckIcon } from '../../../../assets/icons/status-success.svg';
import { ReactComponent as ErrorIcon } from '../../../../assets/icons/xerror.svg';
import { EXTERNAL_DOC_SPECS, OTHER_DOC_SPEC } from '../../../constants/externalDocuments';
import { CLICK_ACTION, SUBMIT_BENEFITS_DOCUMENTS, type BenefitsDocumentsSource } from '../../../constants/tagManager';
import { useExternalBenefitsSubmit } from '../../../hooks/useExternalBenefitsSubmit/useExternalBenefitsSubmit';
import type { IExternalDocSpec, IQuote } from '../../../interfaces/quotes';
import TagManagerService from '../../../services/gtm/tagManagerService';
import { TRANSLATIONS_EXTERNAL_BENEFITS } from '../../../translations/commons';
import { getExternalOriginalName, groupExternalFilesByType } from '../../../utils/quotes/quotes';
import CustomButton from '../../Buttons/CustomButton/CustomButton';
import BannerFill from '../../Globals/BannerFill/BannerFill';
import { CustomModal } from '../../Modals/CustomModal/CustomModal';
import DocumentSlot, { DocumentSlotList, type DocumentSlotFile } from '../DocumentSlot/DocumentSlot';

import './ExternalBenefitsDocumentsModal.scss';

const MODAL = TRANSLATIONS_EXTERNAL_BENEFITS.modal;

export interface ExternalBenefitsDocumentsModalProps {
  open: boolean;
  quote: IQuote;
  brokerId: string;
  prospectId: number;
  source: BenefitsDocumentsSource;
  onClose: () => void;
  onSubmitted: (updated: IQuote) => void;
}

export default function ExternalBenefitsDocumentsModal({
  open,
  quote,
  brokerId,
  prospectId,
  source,
  onClose,
  onSubmitted
}: Readonly<ExternalBenefitsDocumentsModalProps>) {
  const { t } = useTranslation();

  const draft = useExternalBenefitsSubmit({ brokerId, prospectId, quote, onSubmitted });
  const { staged, removals, errors, submitting, failed, submitted, hasChanges, hasRemovals } = draft;

  const hasSentFor = (documentType: IExternalDocSpec['documentType']) =>
    groupExternalFilesByType(quote)[documentType].some((file) => !removals.has(file.id));

  const computeOrder = (): IExternalDocSpec[] => {
    const pending = (spec: IExternalDocSpec) => !hasSentFor(spec.documentType);
    return [...EXTERNAL_DOC_SPECS.filter(pending), ...EXTERNAL_DOC_SPECS.filter((spec) => !pending(spec))];
  };

  const [specOrder, setSpecOrder] = useState<IExternalDocSpec[]>(computeOrder);
  const [showOther, setShowOther] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);

  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      draft.reset();
      setShowOther(false);
      setSpecOrder(computeOrder());
    }
  }

  const hasAnyExternal = [...EXTERNAL_DOC_SPECS, OTHER_DOC_SPEC].some(
    (spec) => groupExternalFilesByType(quote)[spec.documentType].length > 0
  );

  const sentSlotFiles = (spec: IExternalDocSpec): DocumentSlotFile[] =>
    groupExternalFilesByType(quote)
      [spec.documentType].filter((file) => !removals.has(file.id))
      .map((file) => ({ id: String(file.id), name: getExternalOriginalName(file) }));

  const stagedSlotFiles = (spec: IExternalDocSpec): DocumentSlotFile[] =>
    staged[spec.documentType].map((entry) => ({ id: entry.id, name: entry.file.name }));

  const renderSlot = (spec: IExternalDocSpec) => {
    const sent = sentSlotFiles(spec);
    const error = errors[spec.documentType];
    return (
      <DocumentSlot
        key={spec.documentType}
        title={t(spec.titleKey)}
        description={sent.length === 0 && spec.descriptionKey ? t(spec.descriptionKey) : undefined}
        accept={spec.accept}
        multiple={spec.multiple}
        files={stagedSlotFiles(spec)}
        sentFiles={sent}
        error={error ? t(error) : undefined}
        onAdd={(files) => draft.addFiles(spec.documentType, files)}
        onRemove={(id) => draft.removeStaged(spec.documentType, id)}
        onRemoveSent={(id) => draft.removeSent(Number(id))}
      />
    );
  };

  const showOtherSlot = showOther || sentSlotFiles(OTHER_DOC_SPEC).length > 0;

  const handleSubmit = () => {
    TagManagerService.pushEvent(CLICK_ACTION, SUBMIT_BENEFITS_DOCUMENTS, { source });
    draft.submit();
  };

  const closeLabel = hasChanges ? t(MODAL.closeWithoutSubmitting) : t(MODAL.close);
  const submitLabel = hasRemovals ? t(MODAL.saveChanges) : t(MODAL.submit);

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={submitted ? undefined : t(MODAL.title)}
      size="xl"
      hideCloseButton
      disableEscapeClose
      customClass="external-benefits-modal"
    >
      {submitted ? (
        <div className="external-benefits-modal__result">
          <span className="external-benefits-modal__result-icon">
            <CheckIcon width={32} height={32} />
          </span>
          <h3 className="external-benefits-modal__result-title">{t(MODAL.confirmationTitle)}</h3>
          <p className="external-benefits-modal__result-text">{t(MODAL.confirmationBody)}</p>
          <div className="external-benefits-modal__actions">
            <CustomButton variant="primary" size="m" onClick={onClose}>
              {t(MODAL.done)}
            </CustomButton>
          </div>
        </div>
      ) : (
        <div className="external-benefits-modal__body">
          {!hasAnyExternal && <p className="external-benefits-modal__subtitle">{t(MODAL.subtitle)}</p>}

          {failed && (
            <div className="external-benefits-modal__error">
              <BannerFill
                variant="danger"
                inline
                icon={<ErrorIcon aria-hidden="true" />}
                title={t(MODAL.error)}
                screenReaderLabel={t(MODAL.error)}
              />
            </div>
          )}

          <DocumentSlotList>{specOrder.map(renderSlot)}</DocumentSlotList>

          {showOtherSlot ? (
            <DocumentSlotList>{renderSlot(OTHER_DOC_SPEC)}</DocumentSlotList>
          ) : (
            <button type="button" className="external-benefits-modal__disclosure" onClick={() => setShowOther(true)}>
              {t(MODAL.otherDisclosure)}
            </button>
          )}

          <div className="external-benefits-modal__footer">
            <CustomButton variant="default" size="m" onClick={onClose} disabled={submitting}>
              {closeLabel}
            </CustomButton>
            <CustomButton variant="primary" size="m" onClick={handleSubmit} loading={submitting} disabled={!hasChanges}>
              {submitLabel}
            </CustomButton>
          </div>
        </div>
      )}
    </CustomModal>
  );
}
